import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import toast from 'react-hot-toast';
import mt5Api from '../../services/mt5ApiService';
import LoaderOverlay from '../common/LoaderOverlay';
import { LEVERAGE_OPTIONS } from '../../constants/accountOptions';
import SourceCombobox from '../common/SourceCombobox';

export interface EditLeverageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  account: {
    login: string | number;
    currentLeverage?: string | number;
    name?: string;
  } | null;
}

const formatLeverageDisplay = (
  val: string | number | undefined | null
): string => {
  if (val === undefined || val === null || String(val).trim() === '') {
    return '100';
  }
  const str = String(val).trim();
  if (str.startsWith('1:')) {
    return str.substring(2);
  }
  return str;
};

const getOptionLabel = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string
) => {
  if (!value) return '';
  const cleanVal = value.startsWith('1:') ? value.substring(2) : value;
  const matched = options.find(
    option => option.value === cleanVal || option.label === value
  );
  return matched
    ? matched.label
    : value.startsWith('1:')
      ? value
      : `1:${value}`;
};

export const EditLeverageModal: React.FC<EditLeverageModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  account,
}) => {
  const [newLeverage, setNewLeverage] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const existingLeverage = formatLeverageDisplay(account?.currentLeverage);

  useEffect(() => {
    if (isOpen) {
      setNewLeverage(existingLeverage !== '100' ? existingLeverage : '');
      setReason('');
      setIsSubmitting(false);
    } else {
      document.body.style.pointerEvents = '';
    }

    return () => {
      document.body.style.pointerEvents = '';
    };
  }, [isOpen, account, existingLeverage]);

  const handleClose = () => {
    if (isSubmitting) return;
    setNewLeverage('');
    setReason('');
    setIsSubmitting(false);
    document.body.style.pointerEvents = '';
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!account?.login) {
      toast.error('Account Login ID is missing');
      return;
    }

    const cleanLeverageVal = newLeverage.startsWith('1:')
      ? newLeverage.substring(2)
      : newLeverage;

    const levNum = Number(cleanLeverageVal);
    if (!cleanLeverageVal || isNaN(levNum) || levNum <= 0) {
      toast.error('Please enter a valid positive number for new leverage');
      return;
    }

    if (!reason.trim()) {
      toast.error('Please provide a reason for changing leverage');
      return;
    }

    setIsSubmitting(true);

    try {
      await mt5Api.changeLeverage({
        login: Number(account.login),
        leverage: levNum,
        reason: reason.trim(),
      });

      toast.success(`Leverage successfully updated to ${levNum}`);
      setNewLeverage('');
      setReason('');
      setIsSubmitting(false);
      document.body.style.pointerEvents = '';

      onClose();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error changing leverage:', err);
      const errMsg = err?.message || 'Failed to change leverage';
      toast.error(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const loginId = account?.login ? String(account.login) : '-';

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-md w-[95vw] max-h-[90vh] flex flex-col p-0 overflow-hidden rounded-xl shadow-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
        {/* Sticky Header */}
        <DialogHeader className="p-2 sm:p-2 border-b border-gray-100 dark:border-gray-800 shrink-0 space-y-1">
          <div className="flex items-center gap-3">
            <div>
              <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
                Edit Leverage
              </DialogTitle>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Update leverage parameters for MT5 Account
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Form Body */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-between"
        >
          <div className="sm:p-2 space-y-2">
            {/* Account Info Summary Card */}
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4">
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  MT5 Login ID
                </span>
                <p className="font-mono text-base font-bold text-gray-900 dark:text-white">
                  {loginId}
                </p>
              </div>
              {account?.name && (
                <div className="text-right">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Account Holder
                  </span>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate max-w-[160px]">
                    {account.name}
                  </p>
                </div>
              )}
            </div>

            {/* Existing & New Leverage Fields (Side by Side in 2-Column Responsive Grid) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Existing Leverage
                </Label>
                <Input
                  value={getOptionLabel(LEVERAGE_OPTIONS, existingLeverage)}
                  disabled
                  className="bg-gray-100 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 font-semibold cursor-not-allowed border-gray-200 dark:border-gray-700"
                />
                <span className="text-[11px] text-gray-400 block leading-tight">
                  Currently assigned leverage
                </span>
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="newLeverage"
                  className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  New Leverage <span className="text-red-500">*</span>
                </Label>
                <SourceCombobox
                  id="newLeverage"
                  items={LEVERAGE_OPTIONS.map(leverage => leverage.label)}
                  placeholder="Select leverage"
                  value={getOptionLabel(LEVERAGE_OPTIONS, newLeverage)}
                  onChange={value => {
                    const leverage = LEVERAGE_OPTIONS.find(
                      option => option.label === value || option.value === value
                    );
                    if (leverage) {
                      setNewLeverage(leverage.value);
                    } else {
                      const cleanVal = value.startsWith('1:')
                        ? value.substring(2)
                        : value;
                      setNewLeverage(cleanVal);
                    }
                  }}
                  disabled={isSubmitting}
                  emptyText="No leverage options found."
                />
                <span className="text-[11px] text-gray-400 block leading-tight">
                  Select leverage ratio
                </span>
              </div>
            </div>

            {/* Reason Field */}
            <div className="space-y-1.5">
              <Label
                htmlFor="reason"
                className="text-sm font-semibold text-gray-700 dark:text-gray-300"
              >
                Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="reason"
                placeholder="e.g. Client requested higher leverage"
                value={reason}
                onChange={e => setReason(e.target.value)}
                disabled={isSubmitting}
                rows={3}
                className="w-full resize-none border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 text-sm"
                required
              />
            </div>
          </div>

          {/* Sticky Footer */}
          <DialogFooter className="p-2 sm:p-2 border-t border-gray-100 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-900 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="w-full sm:w-auto text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !newLeverage || !reason.trim()}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm transition-all"
            >
              {isSubmitting ? (
                <LoaderOverlay isLoading={true} loadingText="Updating..." />
              ) : (
                'Submit'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditLeverageModal;
